import { Aviso, ETIQUETA_MOTIVO, MOTIVOS_HISTORICOS, MOTIVOS_SIN_META, MotivoSinMeta } from '../types/metas.types';
import { esFilaCasa } from './casa';

/**
 * Reglas de UNA celda — espejo de `validarCelda` en
 * `cdr-sa-api/src/modules/metas-colaborador/domain/validar-captura.ts`, con los
 * mismos códigos y los mismos mensajes.
 *
 * El servidor es la autoridad: esto sólo existe para marcar el renglón
 * MIENTRAS se escribe, en vez de enterarse al guardar con un 422. Las
 * advertencias (meta baja, fuera de rango, suma contra la tienda) NO se
 * replican aquí: dependen de parámetros y datos que decide el servidor, y dos
 * definiciones de lo mismo acaban diciendo cosas distintas.
 */
export interface CeldaValidable {
  key: string;
  nombre: string;
  metaMonto: number | null | undefined;
  sinMetaMotivo: MotivoSinMeta | string | null | undefined;
  sinMetaDetalle?: string | null;
  apoyo?: { chain: string; num: number; dias: number } | null;
  /** La Casa (ronda F) sólo lleva meta: las marcas por persona tampoco. */
  esCasa?: boolean;
  transporte?: boolean;
  bodegueroFijo?: boolean;
  diasIncapacidad?: number | null;
}

/**
 * Motivos que ya no se capturan: los históricos («Cajero/a», «Gerente»: ahora
 * lo dice el puesto) y «Baja», que la migración pasó a «Baja en proceso».
 */
const RETIRADOS = [...MOTIVOS_HISTORICOS, 'baja'] as string[];

/**
 * Tope de una meta semanal POR PERSONA — el mismo `META_MAXIMA` de la API
 * (`domain/validar-captura.ts` y el `@Max` del DTO). La tienda más grande
 * vende ~$2 M a la semana; $10 M sólo sale de una coma mal puesta al pegar.
 *
 * Sin repetirlo aquí, la casilla no se marcaba y el guardado reventaba con un
 * 400 del ValidationPipe que decía «filas.17.metaMonto must not be greater
 * than 10000000»: en inglés y con el NÚMERO de fila, no el nombre.
 *
 * ⚠️ No se pone como `max` del InputNumber: PrimeReact recortaría en silencio
 * 95,000,000 a 10,000,000 y la meta equivocada se guardaría sin avisar.
 */
export const META_MAXIMA = 10_000_000;

/** Un valor que puede estar GUARDADO: capturable o histórico. */
const esMotivo = (v: unknown): v is MotivoSinMeta =>
  typeof v === 'string' && ([...MOTIVOS_SIN_META, ...MOTIVOS_HISTORICOS] as string[]).includes(v);

/**
 * `motivoGuardado` = lo que el servidor tiene guardado para esa persona. Un
 * motivo histórico que YA estaba (tienda reabierta) se conserva mientras nadie
 * lo cambie; ponerlo de nuevo es `MOTIVO_RETIRADO`, como en `validarContraGuardado`
 * de la API.
 */
export function validarCelda(
  f: CeldaValidable,
  tienda: { chain: string; num: number },
  motivoGuardado: string | null = null,
): Aviso[] {
  const out: Aviso[] = [];
  const e = (codigo: string, mensaje: string) =>
    out.push({ codigo, nivel: 'error', key: f.key, mensaje: `${f.nombre}: ${mensaje}` });

  const tieneMeta = f.metaMonto !== null && f.metaMonto !== undefined;
  if (tieneMeta && !(Number.isFinite(f.metaMonto) && (f.metaMonto as number) > 0)) {
    e(
      'META_NO_POSITIVA',
      'la meta va en pesos y mayor que cero. Si no lleva meta, deja la meta vacía y elige el motivo.',
    );
  } else if (tieneMeta && (f.metaMonto as number) > META_MAXIMA) {
    e(
      'META_FUERA_DE_LIMITE',
      `una meta semanal de más de $${META_MAXIMA.toLocaleString('es-MX')} no es de una persona. Revisa que no sobre un cero.`,
    );
  }
  const m = f.sinMetaMotivo;
  if (m !== null && m !== undefined && m !== '') {
    if (m === 'baja') e('MOTIVO_RETIRADO', '«Baja» ya no es un motivo: usa «Baja en proceso».');
    else if (!esMotivo(m)) e('MOTIVO_DESCONOCIDO', `«${m}» no es un motivo de la lista.`);
    else if (RETIRADOS.includes(m) && motivoGuardado !== m)
      e(
        'MOTIVO_RETIRADO',
        `«${ETIQUETA_MOTIVO[m] ?? m}» ya no es un motivo: quién lleva meta lo dice su puesto de BUK. Si no lleva meta por otra razón, elige el motivo.`,
      );
  }
  // La Casa (23-oct-2026, ronda F): sólo lleva meta. La pantalla no ofrece lo
  // demás; esto es por lo que llegue de otro lado (el mismo error de la API).
  // `false` y `null` valen: el admin anterior manda las marcas en falso.
  if (
    esFilaCasa(f) &&
    (!!f.sinMetaMotivo || !!f.apoyo || f.transporte === true || f.bodegueroFijo === true || (f.diasIncapacidad ?? 0) > 0)
  ) {
    e('CASA_SOLO_META', 'la Casa sólo lleva meta: sin motivo, apoyo, transporte, bodeguero fijo ni días de incapacidad.');
  }
  if (tieneMeta && f.sinMetaMotivo) {
    e('META_Y_MOTIVO', 'tiene meta y motivo de «sin meta» a la vez. Quita uno de los dos.');
  }
  if (f.sinMetaMotivo === 'otro' && !(f.sinMetaDetalle ?? '').trim()) {
    e('OTRO_SIN_DETALLE', 'con el motivo «Otro» hay que escribir cuál.');
  }
  if (f.sinMetaMotivo === 'apoyo_otra_tienda' && !f.apoyo) {
    e('APOYO_REQUERIDO', 'con el motivo «Apoyo en otra tienda» hay que decir a qué tienda apoya.');
  }
  if (f.apoyo) {
    const a = f.apoyo;
    if (!a.chain || !Number.isInteger(a.num) || a.num <= 0 || !Number.isInteger(a.dias)) {
      e('APOYO_INCOMPLETO', 'el apoyo necesita la tienda apoyada y los días.');
    } else {
      if (a.dias < 1 || a.dias > 7) e('APOYO_DIAS', 'los días de apoyo van de 1 a 7.');
      // La cadena va en la comparación: la 118 existe en TAF y en TRE, y apoyar
      // a la otra 118 es apoyar a OTRA tienda.
      if (a.chain === tienda.chain && a.num === tienda.num)
        e('APOYO_PROPIA_TIENDA', 'el apoyo es a OTRA tienda, no a la suya.');
    }
  }
  return out;
}

/** Errores de celda de toda la captura, agrupados por la llave de la persona. */
export function erroresPorLlave(
  filas: CeldaValidable[],
  tienda: { chain: string; num: number },
  /** Lo guardado en el servidor, por llave (para `MOTIVO_RETIRADO`). */
  guardadas: { key: string; sinMetaMotivo: string | null }[] = [],
): Map<string, Aviso[]> {
  const m = new Map<string, Aviso[]>();
  const antes = new Map(guardadas.map((g) => [g.key, g.sinMetaMotivo ?? null]));
  for (const f of filas) {
    const errs = validarCelda(f, tienda, antes.get(f.key) ?? null);
    if (errs.length) m.set(f.key, errs);
  }
  return m;
}

/** Nombre legible de los campos que el ValidationPipe de la API nombra en inglés. */
const CAMPO_LEGIBLE: Record<string, string> = {
  metaMonto: 'la meta',
  sinMetaMotivo: 'el motivo sin meta',
  sinMetaDetalle: 'el detalle del motivo',
  apoyo: 'el apoyo',
  dias: 'los días de apoyo',
  num: 'la tienda apoyada',
  chain: 'la cadena de la tienda apoyada',
};

const cifra = (n: string) => {
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString('es-MX') : n;
};

/**
 * Traduce los mensajes del ValidationPipe de Nest que señalan una fila por su
 * POSICIÓN —«filas.17.metaMonto must not be greater than 10000000»— al nombre
 * de la persona de esa fila, que es lo único que el supervisor puede buscar en
 * la tabla. `filas` es lo que se MANDÓ, en el mismo orden (el índice es el del
 * cuerpo del `PUT`). Lo que no reconoce lo devuelve tal cual.
 */
export function traducirMensajeValidacion(mensaje: string, filas: { nombre: string }[]): string {
  const m = /^filas\.(\d+)\.([\w.]+)\s+(.*)$/.exec(mensaje.trim());
  if (!m) return mensaje;
  const fila = filas[Number(m[1])];
  if (!fila) return mensaje;
  const campo = m[2].split('.').pop() ?? m[2];
  const nombreCampo = CAMPO_LEGIBLE[campo] ?? campo;
  const regla = m[3];
  const mayor = /^must not be greater than (-?[\d.]+)$/.exec(regla);
  const menor = /^must not be less than (-?[\d.]+)$/.exec(regla);
  let texto: string;
  if (mayor) {
    texto =
      campo === 'metaMonto'
        ? `la meta no puede pasar de $${cifra(mayor[1])}. Revisa que no sobre un cero.`
        : `el máximo de ${nombreCampo} es ${cifra(mayor[1])}.`;
  } else if (menor) {
    texto = `el mínimo de ${nombreCampo} es ${cifra(menor[1])}.`;
  } else if (/^must be (a number|an integer)/.test(regla)) {
    texto = `${nombreCampo} va en número.`;
  } else {
    texto = `${nombreCampo}: ${regla}`;
  }
  return `${fila.nombre}: ${texto}`;
}
